import {
  FC,
  ReactNode,
  createContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useContext,
  useRef,
  MutableRefObject,
} from "react";
import { AssistantRuntimeProvider, useThread } from "@assistant-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useAISDKRuntime } from "@assistant-ui/react-ai-sdk";
import { useChat } from "@ai-sdk/react";
import type { UIMessage } from "ai";
import { usePostHog } from "posthog-js/react";
import { convertToModelMessages } from "ai";
import {
  ChatRuntimeContext,
  type AgentEventsState,
  type AgentToolCall,
  type AgentInvestigation,
  type AgentProgressStatus,
  type AgentWorkflowStage,
} from "@/chat/ChatRuntimeProvider";
import { MessageIdMappingProvider, useMessageIdMapping } from "@/chat/MessageIdMappingContext";
import { SharepointFileProvider } from "@/chat/SharepointFileContext";
import { ConversationIdProvider, useConversationContext } from "@/chat/ConversationContext";
import { PythonBackendChatTransport } from "@/chat/PythonBackendChatTransport";
import { API_URL } from "@/services/shared";
import { useAuth } from "@/contexts/AuthContext";
import i18n from "@/i18n";
import { trackChatEvent, POSTHOG_EVENTS, type ChatConversationEntryPoint } from "@/utils/posthog";
import type { Message } from "@/types/supabase.ext";
import { useCanvasStore } from "../store";
import { canvasKeys } from "@/canvas/queryKeys";
import { getCanvasDocument } from "../service";
import type { CanvasDocumentContent, CanvasDocumentWithContent } from "../types";
import {
  deleteConversation,
  generateConversationTitle,
  getMessages,
  hasConversationMessages,
} from "@/services/conversationService";
import { useAIDocumentConversation } from "./useAIDocumentConversation";
import { CanvasLockContext } from "../hooks/useCanvasLock";

// --- Conversation switching context ---

/**
 * Existing report-chat rows can still contain the legacy "Report Chat" title from before
 * automatic title generation existed.
 */
const LEGACY_PLACEHOLDER_CONVERSATION_TITLE = "Report Chat";

const isPlaceholderConversationTitle = (
  title: string | null | undefined,
  placeholderTitles: ReadonlySet<string>,
) => !title || placeholderTitles.has(title);

type ConversationTitleMessage = {
  role: string;
  content: Array<{ type: "text"; text: string }>;
};

type ConversationSwitchContextValue = {
  switchToConversation: (conversationId: string) => Promise<void>;
  startNewConversation: () => void;
  activeConversationId: string | undefined;
};

const ConversationSwitchContext = createContext<ConversationSwitchContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useConversationSwitch() {
  const ctx = useContext(ConversationSwitchContext);
  if (!ctx) throw new Error("useConversationSwitch must be used within CanvasChatRuntimeProvider");
  return ctx;
}

const isChatMessageRole = (role: string): role is "user" | "assistant" =>
  role === "user" || role === "assistant";

const toUiConversationMessages = (dbMessages: Message[]): UIMessage[] =>
  dbMessages
    .filter((message): message is Message & { role: "user" | "assistant" } =>
      isChatMessageRole(message.role),
    )
    .map((message) => ({
      id: message.id,
      role: message.role,
      parts: [{ type: "text" as const, text: message.content || "" }],
      createdAt: new Date(message.created_at),
    }));

const toTitleMessagesFromDbMessages = (dbMessages: Message[]): ConversationTitleMessage[] =>
  dbMessages
    .filter((message) => isChatMessageRole(message.role))
    .map((message) => ({
      role: message.role,
      content: [{ type: "text" as const, text: message.content || "" }],
    }));

const toTitleMessagesFromUiMessages = (
  messages: Array<Pick<UIMessage, "role" | "parts">>,
): ConversationTitleMessage[] =>
  messages.map((message) => ({
    role: message.role,
    content: message.parts
      .filter((part): part is { type: "text"; text: string } => part.type === "text")
      .map((part) => ({ type: "text" as const, text: part.text })),
  }));

const getAppendRequestMessages = (messages: UIMessage[]): UIMessage[] => {
  const latestMessage = messages.at(-1);
  return latestMessage?.role === "user" ? [latestMessage] : messages;
};

interface CanvasChatRuntimeProviderProps {
  projectId: string;
  organizationId?: string;
  documentId: string;
  chatOpenSignal?: CanvasChatOpenSignal;
  children: ReactNode;
}

export type CanvasChatOpenSignal = {
  entryPoint: Extract<ChatConversationEntryPoint, "auto_open" | "manual_open">;
  version: number;
};

export const CanvasChatRuntimeProvider: FC<CanvasChatRuntimeProviderProps> = ({
  projectId,
  organizationId,
  documentId,
  chatOpenSignal,
  children,
}) => {
  const { session, user } = useAuth();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const syncFromBackend = useCanvasStore((state) => state.syncFromBackend);
  const setAgentEditingLock = useCanvasStore((state) => state.setAgentEditingLock);
  const placeholderConversationTitles = useMemo(
    () =>
      new Set([
        LEGACY_PLACEHOLDER_CONVERSATION_TITLE,
        // `/api/gen-title` currently persists the Swedish untitled fallback on failures.
        i18n.getFixedT("sv")("chat.newConversation", "Ny konversation"),
      ]),
    [],
  );

  // Report conversation management
  const { ensureConversation, resetConversation, loadConversation, conversationIdRef } =
    useAIDocumentConversation({
      documentId,
      projectId,
    });
  const [activeConversationId, setActiveConversationId] = useState<string | undefined>(undefined);
  const conversationEntryPointRef = useRef<ChatConversationEntryPoint>("existing");

  // Reset conversation when document changes
  useEffect(() => {
    resetConversation();
    setActiveConversationId(undefined);
    conversationEntryPointRef.current = "existing";
  }, [documentId, resetConversation]);

  useEffect(() => {
    if (!chatOpenSignal) return;
    conversationEntryPointRef.current = chatOpenSignal.entryPoint;
  }, [chatOpenSignal]);

  // Agent events state management
  const [agentEvents, setAgentEvents] = useState<AgentEventsState>({
    timeline: [],
    progress: {},
    toolCalls: {},
    investigations: {},
  });

  const resetAgentEvents = useCallback(() => {
    setAgentEvents({
      timeline: [],
      progress: {},
      toolCalls: {},
      investigations: {},
    });
  }, []);

  // Ref for full message content (used by title generation in onFinish to avoid stale closure)
  const chatMessagesRef = useRef<UIMessage[]>([]);

  // Refs for message ID mapping
  // messagesRef holds the latest chat.messages for access in handleAgentEvent
  // setMappingRef holds the setMapping function from MessageIdMappingContext
  // hasMappingRef holds hasMapping to check if a message is already mapped (for multi-turn)
  const messagesRef = useRef<Array<{ id: string; role: string }>>([]);
  const setMappingRef = useRef<((clientId: string, databaseId: string) => void) | null>(null);
  const hasMappingRef = useRef<((clientId: string) => boolean) | null>(null);
  const clearMappingsRef = useRef<(() => void) | null>(null);
  const clearLocalConversationStateRef = useRef<((conversationId?: string) => void) | null>(null);
  const conversationSelectionVersionRef = useRef(0);

  // Ref to hold the ConversationIdProvider's currentIdRef for syncing
  // This is populated by CanvasConversationSync and used by the transport
  const conversationIdContextRef = useRef<React.MutableRefObject<string | undefined> | null>(null);

  // Handle custom SSE events from the backend
  const handleAgentEvent = useCallback(
    async (event: Record<string, unknown>) => {
      // Handle canvas updates (existing logic)
      if (event.type === "canvas-updated") {
        const eventDocId = event.document_id as string;
        const inlineContent = event.content as CanvasDocumentContent | undefined;
        try {
          if (inlineContent) {
            // Use authoritative content sent inline from the server.
            // This avoids a DB roundtrip that could return stale data.
            syncFromBackend(inlineContent);
            queryClient.setQueryData(
              canvasKeys.document(eventDocId),
              (old: CanvasDocumentWithContent | undefined) =>
                old ? { ...old, content: inlineContent } : old,
            );
          } else {
            // Fallback: fetch from DB if content not inline
            const updatedDoc = await getCanvasDocument(eventDocId);
            syncFromBackend(updatedDoc.content);
            queryClient.setQueryData(canvasKeys.document(eventDocId), updatedDoc);
          }
        } catch (error) {
          console.error("Failed to sync canvas update:", error);
          queryClient.invalidateQueries({
            queryKey: canvasKeys.document(eventDocId),
          });
        }
        return;
      }

      // Handle message-saved events for feedback system
      // Backend sends these after storing messages with the database UUID
      //
      // MULTI-TURN SAFETY: Uses "first unmapped message" strategy which is safe because:
      // 1. SSE guarantees event ordering within a stream
      // 2. Each message is mapped exactly once (hasMapping check prevents re-mapping)
      // 3. Messages are added to chat.messages BEFORE backend storage, so messagesRef
      //    always contains the message when its message-saved event arrives
      // 4. Even with concurrent requests, events arrive in storage order, and first-unmapped
      //    correctly associates each event with the oldest unmapped message of that role
      if (event.type === "message-saved") {
        const role = event.role as string;
        const databaseId = event.messageId as string;

        // Validate UUID format before storing (logs error for debugging, doesn't leak value)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(databaseId)) {
          console.error(
            "[CanvasChatRuntimeProvider] Received invalid message ID format from backend",
          );
          return;
        }

        if (role && databaseId && setMappingRef.current && hasMappingRef.current) {
          // Find first UNMAPPED message of this role - safe for multi-turn (see comment above)
          const messages = messagesRef.current;
          const clientMessage = messages.find(
            (m) => m.role === role && !hasMappingRef.current!(m.id),
          );
          if (clientMessage?.id) {
            setMappingRef.current(clientMessage.id, databaseId);
          }
        }
        return;
      }

      // Handle agent status events
      if (event.type === "agent-status") {
        const status = event.status as AgentWorkflowStage | undefined;
        if (status) {
          // Lock autosave while agent is investigating/running tools.
          // Unlock when agent is done/answering so user edits can save.
          if (status === "investigating") {
            setAgentEditingLock(true);
          } else if (status === "done" || status === "answering") {
            setAgentEditingLock(false);
          }
          setAgentEvents((prev) => ({
            ...prev,
            status,
          }));
        }
        return;
      }

      // Handle agent tool-use events
      if (event.type === "agent-tool-use") {
        const investigationId = event.investigation_id as string | undefined;
        const investigationQuestion = event.investigation_question as string | undefined;
        const tool = (event.tool as string) || "unknown";
        const step = typeof event.step === "number" ? event.step : 0;
        const status = (event.status as AgentProgressStatus) || "running";
        const preview = event.preview as string | undefined;
        const error = event.error as string | undefined;

        if (!investigationId) return;

        setAgentEvents((prev) => {
          const activeIteration = prev.activeIteration ?? 1;

          // Create tool call entry
          const toolCall: AgentToolCall = {
            investigationId,
            investigationQuestion,
            tool,
            step,
            status,
            preview,
            error,
            timestamp: Date.now(),
          };

          // Update tool calls for this investigation
          const existingCalls = prev.toolCalls[investigationId] || [];
          const callIndex = existingCalls.findIndex(
            (call) => call.tool === tool && call.step === step,
          );
          const updatedCalls =
            callIndex >= 0
              ? existingCalls.map((c, i) => (i === callIndex ? toolCall : c))
              : [...existingCalls, toolCall];

          // Calculate investigation status
          const calculateStatus = (calls: AgentToolCall[]): AgentProgressStatus => {
            if (calls.length === 0) return "pending";
            if (calls.some((c) => c.status === "failed")) return "failed";
            if (calls.some((c) => c.status === "running")) return "running";
            if (calls.every((c) => c.status === "success")) return "success";
            return "pending";
          };

          // Update investigations for this iteration
          const iterationInvestigations = prev.investigations[activeIteration] || [];
          const invIndex = iterationInvestigations.findIndex((inv) => inv.id === investigationId);
          const updatedInvestigations: AgentInvestigation[] =
            invIndex >= 0
              ? iterationInvestigations.map((inv, i) =>
                  i === invIndex
                    ? {
                        ...inv,
                        toolCalls: updatedCalls,
                        status: calculateStatus(updatedCalls),
                      }
                    : inv,
                )
              : [
                  ...iterationInvestigations,
                  {
                    id: investigationId,
                    question: investigationQuestion || "Updating document...",
                    iteration: activeIteration,
                    toolCalls: updatedCalls,
                    status: calculateStatus(updatedCalls),
                  },
                ];

          return {
            ...prev,
            activeIteration,
            toolCalls: {
              ...prev.toolCalls,
              [investigationId]: updatedCalls,
            },
            investigations: {
              ...prev.investigations,
              [activeIteration]: updatedInvestigations,
            },
          };
        });
        return;
      }
    },
    [queryClient, syncFromBackend, setAgentEditingLock],
  );

  // Reset agent events before each new request
  const onBeforeRequest = useCallback(() => {
    resetAgentEvents();
  }, [resetAgentEvents]);

  const failedDraftCleanupRef = useRef<Promise<void> | null>(null);

  // Create transport pointing to /api/chat-report
  const transport = useMemo(
    () =>
      new PythonBackendChatTransport({
        api: `${API_URL}/api/chat-report`,
        onAgentEvent: handleAgentEvent,
        prepareSendMessagesRequest: async (req) => {
          // Call reset before sending
          onBeforeRequest();

          if (failedDraftCleanupRef.current) {
            await failedDraftCleanupRef.current;
          }

          // Ensure conversation exists before sending
          const { conversationId, wasCreated } = await ensureConversation();
          const conversationEntryPoint = wasCreated ? conversationEntryPointRef.current : undefined;
          setActiveConversationId(conversationId);

          // Sync conversation ID to ConversationIdProvider context for feedback hook
          if (conversationId && conversationIdContextRef.current) {
            conversationIdContextRef.current.current = conversationId;
          }

          // Track conversation creation only when a new DB row was created.
          if (conversationId && wasCreated) {
            trackChatEvent(posthog, POSTHOG_EVENTS.CHAT_CONVERSATION_CREATED, {
              conversation_id: conversationId,
              chat_type: "ai_document",
              conversation_entry_point: conversationEntryPoint,
              project_id: projectId,
              organization_id: organizationId,
              document_id: documentId,
              user_id: user?.id,
              action_outcome: "success",
            });
          }

          // Store conversation ID for success tracking and title generation in onFinish
          pendingMessageRef.current = {
            conversationId,
            wasCreated,
            entryPoint: conversationEntryPoint,
          };

          return {
            ...req,
            headers: {
              ...req.headers,
              Authorization: session?.access_token ? `Bearer ${session.access_token}` : "",
            },
            body: {
              ...req.body,
              document_id: documentId,
              project_id: projectId,
              conversation_id: conversationId,
              messages: convertToModelMessages(getAppendRequestMessages(req.messages)),
            },
          };
        },
      }),
    [
      documentId,
      projectId,
      organizationId,
      session?.access_token,
      handleAgentEvent,
      onBeforeRequest,
      ensureConversation,
      posthog,
      user?.id,
    ],
  );

  // Track conversation ID for success tracking and title generation
  const pendingMessageRef = useRef<{
    conversationId: string | undefined;
    wasCreated: boolean;
    entryPoint?: ChatConversationEntryPoint;
  } | null>(null);

  const keepLocalConversationState = useCallback(
    (conversationId: string) => {
      loadConversation(conversationId);
      setActiveConversationId(conversationId);

      if (conversationIdContextRef.current) {
        conversationIdContextRef.current.current = conversationId;
      }
    },
    [loadConversation],
  );

  const reconcileFailedDraftConversation = useCallback(
    async (conversationId: string) => {
      const conversationHasMessages = await hasConversationMessages(conversationId);
      if (conversationHasMessages) {
        // The backend persisted messages before the request failed, so this draft
        // should stay selected instead of being torn down like an empty draft.
        keepLocalConversationState(conversationId);
        void queryClient.invalidateQueries({
          queryKey: canvasKeys.conversations(documentId),
        });
        return;
      }

      await deleteConversation(conversationId);
      clearLocalConversationStateRef.current?.(conversationId);

      void queryClient.invalidateQueries({
        queryKey: canvasKeys.conversations(documentId),
      });
    },
    [keepLocalConversationState, queryClient, documentId],
  );

  // Track title generation separately so failures can retry without duplicate requests.
  const titleGenerationInFlightRef = useRef<Set<string>>(new Set());
  const titleGeneratedSuccessfullyRef = useRef<Set<string>>(new Set());

  const maybeGenerateConversationTitle = useCallback(
    (conversationId: string, messages: ConversationTitleMessage[]) => {
      if (messages.length === 0) return;
      if (titleGeneratedSuccessfullyRef.current.has(conversationId)) return;
      if (titleGenerationInFlightRef.current.has(conversationId)) return;

      const cachedConversations = queryClient.getQueryData<
        Array<{ id: string; title: string | null }>
      >(canvasKeys.conversations(documentId));
      const conversation = cachedConversations?.find((c) => c.id === conversationId);
      if (!isPlaceholderConversationTitle(conversation?.title, placeholderConversationTitles)) {
        return;
      }

      titleGenerationInFlightRef.current.add(conversationId);

      void generateConversationTitle(conversationId, messages)
        .then((title) => {
          if (!title) return;

          if (!isPlaceholderConversationTitle(title, placeholderConversationTitles)) {
            titleGeneratedSuccessfullyRef.current.add(conversationId);
          }
          void queryClient.invalidateQueries({
            queryKey: canvasKeys.conversations(documentId),
          });
        })
        .finally(() => {
          titleGenerationInFlightRef.current.delete(conversationId);
        });
    },
    [queryClient, documentId, placeholderConversationTitles],
  );

  // Use the AI SDK chat hook with our custom transport
  const chat = useChat({
    transport,
    onFinish: () => {
      // Safety: always release the agent editing lock when stream ends
      setAgentEditingLock(false);
      // Track successful message delivery
      const pending = pendingMessageRef.current;
      if (pending) {
        trackChatEvent(posthog, POSTHOG_EVENTS.CHAT_MESSAGE_SENT, {
          conversation_id: pending.conversationId,
          chat_type: "ai_document",
          project_id: projectId,
          organization_id: organizationId,
          document_id: documentId,
          user_id: user?.id,
          ...(pending.wasCreated
            ? {
                is_first_message_in_conversation: true,
                conversation_entry_point: pending.entryPoint,
              }
            : {}),
          action_outcome: "success",
        });

        void queryClient.invalidateQueries({
          queryKey: canvasKeys.conversations(documentId),
        });

        if (pending.conversationId) {
          const conversationId = pending.conversationId;
          const latestMessages = toTitleMessagesFromUiMessages(chatMessagesRef.current);

          maybeGenerateConversationTitle(conversationId, latestMessages);
        }

        if (pending.wasCreated) {
          conversationEntryPointRef.current = "existing";
        }

        pendingMessageRef.current = null;
      }
    },
    onError: (error) => {
      setAgentEditingLock(false);

      const pending = pendingMessageRef.current;
      pendingMessageRef.current = null;

      if (!pending?.conversationId || !pending.wasCreated) {
        console.error("[CanvasChat] Chat request failed: ", error);
        return;
      }

      const cleanupPromise = reconcileFailedDraftConversation(pending.conversationId)
        .catch((cleanupError) => {
          console.error(
            "[CanvasChat] Failed to clean up draft conversation after chat error: ",
            cleanupError,
          );
        })
        .finally(() => {
          if (failedDraftCleanupRef.current === cleanupPromise) {
            failedDraftCleanupRef.current = null;
          }
        });
      failedDraftCleanupRef.current = cleanupPromise;

      console.error("[CanvasChat] Chat request failed: ", error);
    },
  });

  const isConversationSwitchDisabled = chat.status === "submitted" || chat.status === "streaming";
  const isConversationSwitchDisabledRef = useRef(isConversationSwitchDisabled);
  isConversationSwitchDisabledRef.current = isConversationSwitchDisabled;
  const runtime = useAISDKRuntime(chat);

  // Keep messagesRef updated with latest chat.messages for handleAgentEvent
  // Update synchronously during render (not in useEffect) to avoid race conditions
  // where message-saved events arrive before the useEffect runs
  messagesRef.current = chat.messages.map((m) => ({ id: m.id, role: m.role }));
  chatMessagesRef.current = chat.messages.map(({ id, role, parts }) => ({
    id,
    role,
    parts,
  }));

  const resetThreadMessages = useCallback(
    (messages: UIMessage[] = [], selectionVersion?: number) => {
      if (
        selectionVersion !== undefined &&
        selectionVersion !== conversationSelectionVersionRef.current
      ) {
        return false;
      }

      if (isConversationSwitchDisabledRef.current) {
        return false;
      }

      runtime.thread.reset();
      chat.setMessages(messages);

      return true;
    },
    [runtime, chat],
  );

  const clearLocalConversationState = useCallback(
    (conversationId?: string) => {
      const shouldClearCurrentConversation =
        conversationId === undefined || activeConversationId === conversationId;

      if (conversationId === undefined || conversationIdRef.current === conversationId) {
        resetConversation();
      }

      if (
        (conversationId === undefined ||
          conversationIdContextRef.current?.current === conversationId) &&
        conversationIdContextRef.current
      ) {
        conversationIdContextRef.current.current = undefined;
      }

      if (!shouldClearCurrentConversation) {
        return;
      }

      conversationSelectionVersionRef.current += 1;
      resetAgentEvents();
      setActiveConversationId(undefined);
      clearMappingsRef.current?.();
      resetThreadMessages();
    },
    [
      activeConversationId,
      conversationIdRef,
      resetConversation,
      conversationIdContextRef,
      resetAgentEvents,
      resetThreadMessages,
    ],
  );
  clearLocalConversationStateRef.current = clearLocalConversationState;

  // --- Conversation switching ---
  const switchToConversation = useCallback(
    async (conversationId: string) => {
      if (isConversationSwitchDisabled) return;
      const selectionVersion = ++conversationSelectionVersionRef.current;

      const dbMessages = await getMessages(conversationId);
      if (selectionVersion !== conversationSelectionVersionRef.current) return;
      if (isConversationSwitchDisabledRef.current) return;

      const uiMessages = toUiConversationMessages(dbMessages);
      const didReset = resetThreadMessages(uiMessages, selectionVersion);
      if (!didReset) return;

      resetAgentEvents();
      loadConversation(conversationId);
      setActiveConversationId(conversationId);
      clearMappingsRef.current?.();

      // Sync conversation ID to context for feedback
      if (conversationIdContextRef.current) {
        conversationIdContextRef.current.current = conversationId;
      }

      conversationEntryPointRef.current = "picker_switch";
      maybeGenerateConversationTitle(conversationId, toTitleMessagesFromDbMessages(dbMessages));
    },
    [
      loadConversation,
      conversationIdContextRef,
      maybeGenerateConversationTitle,
      isConversationSwitchDisabled,
      resetAgentEvents,
      resetThreadMessages,
    ],
  );

  const startNewConversation = useCallback(() => {
    if (isConversationSwitchDisabled) return;
    conversationEntryPointRef.current = "picker_new_chat";
    clearLocalConversationState();
  }, [clearLocalConversationState, isConversationSwitchDisabled]);

  const conversationSwitchValue = useMemo(
    () => ({
      switchToConversation,
      startNewConversation,
      activeConversationId,
    }),
    [switchToConversation, startNewConversation, activeConversationId],
  );

  // Minimal ChatRuntimeContext value for canvas (not all features needed)
  // Note: Using "project" for convoType as the UI (ThreadWelcome, feedback) expects this value.
  // PostHog tracking uses chat_type: "ai_document" separately in the tracking calls above.
  // documentId is set to indicate this is a canvas chat - tracking is handled by this provider's onFinish.
  const contextValue = useMemo(
    () => ({
      convoType: "project" as const,
      projectId,
      organizationId,
      amaCode: undefined,
      documentId,
      agentEvents,
      resetAgentEvents,
    }),
    [projectId, organizationId, documentId, agentEvents, resetAgentEvents],
  );

  return (
    <ConversationIdProvider>
      <CanvasConversationSync
        conversationIdRef={conversationIdRef}
        conversationIdContextRef={conversationIdContextRef}
        documentId={documentId}
      />
      <ChatRuntimeContext.Provider value={contextValue}>
        <ConversationSwitchContext.Provider value={conversationSwitchValue}>
          <MessageIdMappingProvider>
            <MessageIdMappingSync
              setMappingRef={setMappingRef}
              hasMappingRef={hasMappingRef}
              clearMappingsRef={clearMappingsRef}
              documentId={documentId}
            />
            <SharepointFileProvider convoType="project">
              <AssistantRuntimeProvider runtime={runtime}>
                <ChatLockSync />
                {children}
              </AssistantRuntimeProvider>
            </SharepointFileProvider>
          </MessageIdMappingProvider>
        </ConversationSwitchContext.Provider>
      </ChatRuntimeContext.Provider>
    </ConversationIdProvider>
  );
};

/**
 * Component that syncs the Canvas conversation ID to the ConversationIdProvider context.
 *
 * Canvas chat uses useAIDocumentConversation (its own conversationIdRef), but the feedback
 * hook uses ConversationIdProvider context. This component bridges them by:
 * 1. Exposing context's currentIdRef via conversationIdContextRef (for transport to sync)
 * 2. Syncing conversationIdRef → currentIdRef on render (for existing conversations)
 */
const CanvasConversationSync: FC<{
  conversationIdRef: MutableRefObject<string | undefined>;
  conversationIdContextRef: MutableRefObject<React.MutableRefObject<string | undefined> | null>;
  documentId: string;
}> = ({ conversationIdRef, conversationIdContextRef, documentId }) => {
  const { currentIdRef } = useConversationContext();
  const prevDocumentIdRef = useRef(documentId);

  // Expose context's ref so transport can sync after ensureConversation
  useEffect(() => {
    conversationIdContextRef.current = currentIdRef;
    return () => {
      conversationIdContextRef.current = null;
    };
  }, [currentIdRef, conversationIdContextRef]);

  // Clear conversation ID when document changes (prevents stale ID after switch)
  if (prevDocumentIdRef.current !== documentId) {
    currentIdRef.current = undefined;
    prevDocumentIdRef.current = documentId;
  }

  // Sync existing conversation ID to context on render
  // (Transport handles new conversations via conversationIdContextRef)
  if (conversationIdRef.current && currentIdRef.current !== conversationIdRef.current) {
    currentIdRef.current = conversationIdRef.current;
  }

  return null;
};

/**
 * Component that syncs the message ID mapping functions to the parent refs.
 * This allows handleAgentEvent callback (defined outside the provider) to
 * call setMapping/hasMapping when it receives message-saved events.
 * Also clears mappings when documentId changes (e.g., switching AI Documents).
 */
const MessageIdMappingSync: FC<{
  setMappingRef: MutableRefObject<((clientId: string, databaseId: string) => void) | null>;
  hasMappingRef: MutableRefObject<((clientId: string) => boolean) | null>;
  clearMappingsRef: MutableRefObject<(() => void) | null>;
  documentId: string;
}> = ({ setMappingRef, hasMappingRef, clearMappingsRef, documentId }) => {
  const { setMapping, hasMapping, clearMappings } = useMessageIdMapping();
  const prevDocumentIdRef = useRef(documentId);

  useEffect(() => {
    setMappingRef.current = setMapping;
    hasMappingRef.current = hasMapping;
    clearMappingsRef.current = clearMappings;
    return () => {
      setMappingRef.current = null;
      hasMappingRef.current = null;
      clearMappingsRef.current = null;
    };
  }, [setMapping, hasMapping, clearMappings, setMappingRef, hasMappingRef, clearMappingsRef]);

  // Clear mappings when document changes to prevent stale mappings
  useEffect(() => {
    if (prevDocumentIdRef.current !== documentId) {
      clearMappings();
      prevDocumentIdRef.current = documentId;
    }
  }, [documentId, clearMappings]);

  return null;
};

/**
 * Component that syncs the thread's running state to the CanvasLockContext.
 *
 * Component tree (ChatLockSync IS inside CanvasLockProvider):
 *   CanvasWithChat
 *     └── CanvasLockProvider  ← Context provider
 *           └── CanvasChatPanel
 *                 └── CanvasChatRuntimeProvider
 *                       └── AssistantRuntimeProvider
 *                             └── ChatLockSync  ← Can access CanvasLockContext ✓
 *
 * Must be rendered inside AssistantRuntimeProvider (for useThread).
 * CanvasLockProvider is an ancestor via CanvasWithChat > CanvasChatPanel chain.
 */
const ChatLockSync: FC = () => {
  const isRunning = useThread((thread) => thread.isRunning);

  // Access lock context directly - safely handles null if CanvasLockProvider isn't mounted
  const lockContext = useContext(CanvasLockContext);

  useEffect(() => {
    lockContext?.setIsChatResponding(isRunning);

    // Cleanup: reset lock state on unmount to prevent stale locked state
    return () => {
      lockContext?.setIsChatResponding(false);
    };
  }, [isRunning, lockContext]);

  return null;
};
